// Displays and windows → where the cat can be. Coordinates: global DIP across all displays (y down).
//   Platforms: each display's floor (its taskbar top, or its bottom), window top edges, window bottom edges seen from inside, and
//   the tops of the icons on the desktop (perches: narrow, nothing to climb; behind every window, so only where they show).
//   Walls: window sides, and each display's left / right margin where no other display continues it (where one display's floor is
//   higher than the next one's, the margin between them is a wall the cat can climb up and over).
// Parts hidden behind windows in front are cut away; floors of side-by-side displays at the same height join into one.

const MERGE_Y = 3;

/** Cut [a0, a1] by the intervals in `cuts`. */
function subtract(a0, a1, cuts) {
  let segs = [[a0, a1]];
  for (const [c0, c1] of cuts) segs = segs.flatMap(([s0, s1]) => (c1 <= s0 || c0 >= s1 ? [[s0, s1]] : [[s0, c0], [c1, s1]].filter(([u, v]) => v - u > 1)));
  return segs;
}

/**
 * raw: { displays: [{ id, x, y, w, h, floor }] (primary first), windows: [{ id, rect: {x, y, w, h}, perch? }] front to back }
 *   (perch: a desktop icon — only its top, to sit on, however narrow; they come last, behind the windows)
 *   (or the old single-display form { width, height, floor, windows }). H: the cat's standing height in px.
 * Platforms: { keys, y, x0, x1 } (keys: which edges make it up — 'floor:<display>' or '<window>:top|bottom' — to follow it when
 * windows move). Walls: { key, win, side: 'left' | 'right', x, y0, y1, top } — `side` is the side of the space the wall bounds:
 * a 'left' wall is climbed facing right (a window's left side, a display's right margin).
 */
export function buildWorld(raw, H) {
  const displays = raw.displays ?? [{ id: 'main', x: 0, y: 0, w: raw.width, h: raw.height, floor: raw.floor }];
  const windows = raw.windows ?? [];
  const flat = displays.map((d) => ({ keys: [`floor:${d.id}`], y: d.floor, x0: d.x, x1: d.x + d.w }));
  const walls = [];
  const coverX = (front, y) => front.filter((w) => y > w.rect.y && y < w.rect.y + w.rect.h).map((w) => [w.rect.x, w.rect.x + w.rect.w]);
  const coverY = (front, x) => front.filter((w) => x > w.rect.x && x < w.rect.x + w.rect.w).map((w) => [w.rect.y, w.rect.y + w.rect.h]);
  windows.forEach((w, i) => {
    const r = w.rect;
    const front = windows.slice(0, i);
    const yb = r.y + r.h;
    if (w.perch) {
      // A desktop icon: its top, where it shows (no windows over it) and there is room above it for the cat
      const d = displays.find((e) => r.x + r.w / 2 >= e.x && r.x + r.w / 2 < e.x + e.w && r.y >= e.y && r.y < e.floor);
      if (d && r.y - H * 1.05 > d.y)
        for (const [a, b] of subtract(Math.max(d.x, r.x), Math.min(d.x + d.w, r.x + r.w), coverX(front, r.y))) if (b - a >= H * 0.4) flat.push({ keys: [`${w.id}:top`], y: r.y, x0: a, x1: b });
      return;
    }
    for (const d of displays) {
      const L = Math.max(d.x, r.x);
      const R = Math.min(d.x + d.w, r.x + r.w);
      if (R - L < H * 0.8) continue;
      // Top edge: needs room above for the cat, on this display
      if (r.y - H * 1.05 > d.y && r.y < d.floor - H * 0.5)
        for (const [a, b] of subtract(L, R, coverX(front, r.y))) if (b - a >= H * 0.8) flat.push({ keys: [`${w.id}:top`], y: r.y, x0: a, x1: b });
      // Bottom edge, standing inside the window
      if (yb < d.floor - MERGE_Y && yb > d.y + H * 1.2 && r.h > H * 1.4)
        for (const [a, b] of subtract(L, R, coverX(front, yb - 1))) if (b - a >= H * 0.8) flat.push({ keys: [`${w.id}:bottom`], y: yb, x0: a, x1: b });
    }
    for (const side of ['left', 'right']) {
      const x = side === 'left' ? r.x : r.x + r.w;
      // Sides close to a display's margin are left to the margin wall
      const d = displays.find((e) => x > e.x + H * 0.6 && x < e.x + e.w - H * 0.6 && yb > e.y && r.y < e.floor);
      if (!d) continue;
      for (const [a, b] of subtract(Math.max(d.y, r.y), Math.min(d.floor, yb), coverY(front, x))) if (b - a > H) walls.push({ key: `${w.id}:${side}`, win: w.id, side, x, y0: a, y1: b, top: r.y });
    }
  });
  // Display margins: the parts of a display's left / right edge with no display continuing it
  for (const d of displays) {
    for (const edge of ['left', 'right']) {
      const x = edge === 'left' ? d.x : d.x + d.w;
      const probe = edge === 'left' ? x - 1 : x + 1;
      const cuts = displays.filter((e) => e !== d && probe >= e.x && probe < e.x + e.w).map((e) => [e.y, e.y + e.h]);
      for (const [a, b] of subtract(d.y, d.floor, cuts))
        if (b - a > H) walls.push({ key: `screen:${d.id}:${edge}`, win: null, screen: d.id, side: edge === 'left' ? 'right' : 'left', x, y0: a, y1: b, top: a });
    }
  }
  // Platforms at the same height that touch become one
  flat.sort((p, q) => p.y - q.y || p.x0 - q.x0);
  const platforms = [];
  for (const p of flat) {
    const m = platforms.find((q) => Math.abs(q.y - p.y) <= MERGE_Y && p.x0 <= q.x1 + 4 && p.x1 >= q.x0 - 4);
    if (m) {
      m.x0 = Math.min(m.x0, p.x0);
      m.x1 = Math.max(m.x1, p.x1);
      m.keys.push(...p.keys);
    } else platforms.push({ ...p, keys: [...p.keys] });
  }
  platforms.forEach((p, i) => (p.id = i));
  const floors = new Map(displays.map((d) => [d.id, platforms.find((p) => p.keys.includes(`floor:${d.id}`))]));
  const minX = Math.min(...displays.map((d) => d.x));
  const maxX = Math.max(...displays.map((d) => d.x + d.w));
  return {
    H,
    displays,
    platforms,
    walls,
    minX,
    maxX,
    width: maxX - minX, // (single display: the old meaning)
    height: Math.max(...displays.map((d) => d.y + d.h)) - Math.min(...displays.map((d) => d.y)),
    floorPlatform: floors.get(displays[0].id),
    floors,
    rects: new Map(windows.map((w) => [w.id, w.rect])),
  };
}

/**
 * The display whose floor is first at or below (x, y) (edges count for both displays meeting there); below every floor there,
 * the lowest one; off every display, the nearest one. Without y: the one reaching lowest.
 */
export function displayAt(world, x, y = Infinity) {
  const under = world.displays.filter((d) => x >= d.x && x <= d.x + d.w);
  const below = under.filter((d) => d.floor >= y - 2).sort((a, b) => a.floor - b.floor)[0];
  if (below) return below;
  if (under.length) return under.sort((a, b) => b.floor - a.floor)[0];
  return [...world.displays].sort((a, b) => Math.min(Math.abs(x - a.x), Math.abs(x - a.x - a.w)) - Math.min(Math.abs(x - b.x), Math.abs(x - b.x - b.w)))[0];
}

/** The floor the cat at (x, y) would come down on (see displayAt). */
export function floorAt(world, x, y) {
  return world.floors.get(displayAt(world, x, y).id) ?? world.floorPlatform;
}

/** The platform the cat stands on at (x, y), within a few px. */
export function platformAt(world, x, y, tol = 4) {
  return world.platforms.find((p) => Math.abs(p.y - y) <= tol && x >= p.x0 - 2 && x <= p.x1 + 2) ?? null;
}

/** The highest platform at or below y under x (the floor of that display at worst). */
export function platformBelow(world, x, y) {
  let best = null;
  for (const p of world.platforms) if (p.y >= y - 1 && x >= p.x0 && x <= p.x1 && (!best || p.y < best.y)) best = p;
  return best ?? floorAt(world, x, y);
}

/** The same platform after the windows changed (by the window edges it is made of). */
export function samePlatform(world, old) {
  if (!old) return null;
  return world.platforms.find((p) => p.keys.some((k) => old.keys.includes(k))) ?? null;
}
